// Register a service worker, request Notification permission, and POST the
// resulting Push subscription to the backend. One shared helper for the staff
// PWA (/app) and the client Trac PWA (/trac).
import { useEffect } from "react";

function urlB64ToUint8Array(b64) {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * @param {object} opts
 * @param {string} opts.swPath       e.g. "/staff-sw.js" or "/trac-sw.js"
 * @param {string} opts.publicKeyUrl endpoint returning { public_key }
 * @param {string} opts.subscribeUrl endpoint for POST subscription payload
 * @param {function(url, body): Promise} opts.post async POST function (sends auth headers)
 * @param {boolean} opts.enabled     only run when user/client is signed in
 */
export function usePushRegistration({ swPath, publicKeyUrl, subscribeUrl, post, enabled }) {
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    let cancelled = false;

    (async () => {
      try {
        const reg = await navigator.serviceWorker.register(swPath);
        try { await navigator.serviceWorker.ready; } catch { /* noop */ }
        if (Notification.permission === "default") {
          try { await Notification.requestPermission(); } catch { /* noop */ }
        }
        if (Notification.permission !== "granted") return;
        const keyResp = await fetch(publicKeyUrl);
        if (!keyResp.ok) return;
        const { public_key: publicKey } = await keyResp.json();
        if (!publicKey) return;
        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlB64ToUint8Array(publicKey),
          });
        }
        if (cancelled) return;
        await post(subscribeUrl, sub.toJSON());
      } catch (err) {
        // Non-fatal — permission denied, blocked, or offline. Bell polling still works.
        // eslint-disable-next-line no-console
        console.warn("push registration skipped:", err && err.message);
      }
    })();

    return () => { cancelled = true; };
  }, [enabled, swPath, publicKeyUrl, subscribeUrl, post]);
}
